import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

const TEAL = "#00A8B3";
const NAVY = "#1A1F36";
const MID = "#6B7280";

const scrollTo = (e, href) => {
  e.preventDefault();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const floatingPhotos = [
  { src: "https://images.unsplash.com/photo-1659353886973-ced1dfeab3ac?w=200&h=200&fit=crop&crop=face",
    pos: "top-[10%] left-[10%]", size: "w-28 h-28", delay: 0,
    arrowPos: { bottom: '-12px', right: '-8px', transform: 'rotate(135deg)' } },
  { src: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=200&h=200&fit=crop&crop=face",
    pos: "top-[8%] right-[10%]", size: "w-24 h-24", delay: 0.5,
    arrowPos: { bottom: '-12px', left: '-8px', transform: 'rotate(-135deg)' } },
  { src: "https://images.unsplash.com/photo-1741707039536-113e200f9e0d?w=200&h=200&fit=crop&crop=face",
    pos: "bottom-[16%] left-[8%]", size: "w-[108px] h-[108px]", delay: 1,
    arrowPos: { top: '-12px', right: '-8px', transform: 'rotate(45deg)' } },
  { src: "https://images.unsplash.com/photo-1659353889416-fab1265eaa7f?w=200&h=200&fit=crop&crop=face",
    pos: "bottom-[14%] right-[8%]", size: "w-[100px] h-[100px]", delay: 1.5,
    arrowPos: { top: '-12px', left: '-8px', transform: 'rotate(-45deg)' } },
];

const FloatingAvatar = ({ photo, inView }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={inView ? { opacity: 1, scale: 1 } : {}}
    transition={{ delay: photo.delay + 0.8, duration: 0.5, type: "spring", stiffness: 200 }}
    className={`absolute ${photo.pos} hidden lg:block`}
  >
    <motion.div
      animate={{ y: [0, -10, 0], rotate: [0, 3, -3, 0] }}
      transition={{ duration: 5 + photo.delay, repeat: Infinity, ease: "easeInOut" }}
    >
      {/* Oval/egg shape like confido */}
      <div className={`${photo.size} rounded-full overflow-hidden`}
        style={{ border: `3px solid #1A5F7A`, boxShadow: '0 8px 30px rgba(26,95,122,0.25), inset 0 0 0 2px rgba(255,255,255,0.3)' }}>
        <img src={photo.src} alt="Healthcare professional" className="w-full h-full object-cover" />
      </div>
      {/* Arrow pointing toward center */}
      <motion.div
        animate={{ rotate: [0, 5, -5, 0] }}
        transition={{ duration: 3 + photo.delay, repeat: Infinity }}
        className="absolute"
        style={photo.arrowPos}
      >
        <svg width="20" height="20" viewBox="0 0 20 20">
          <polygon points="10,0 20,20 0,20" fill="#1A5F7A" />
        </svg>
      </motion.div>
    </motion.div>
  </motion.div>
);

export const HeroSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  return (
    <section id="home" ref={ref} className="relative min-h-[88vh] flex items-center overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #E0F7FA 0%, #F0F8FF 40%, #E8F5E9 70%, #F0F8FF 100%)' }}>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden
        style={{
          backgroundImage: 'linear-gradient(rgba(0,168,179,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,168,179,0.04) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
        }} />

      {/* Gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.1, 0.05] }} transition={{ duration: 8, repeat: Infinity }}
          className="absolute top-[15%] right-[15%] w-80 h-80 rounded-full blur-3xl" style={{ background: `${TEAL}10` }} />
        <motion.div animate={{ scale: [1, 1.15, 1], opacity: [0.03, 0.07, 0.03] }} transition={{ duration: 10, repeat: Infinity }}
          className="absolute bottom-[20%] left-[10%] w-64 h-64 rounded-full blur-3xl" style={{ background: '#28A74508' }} />
      </div>

      {/* Floating AI agent photos */}
      {floatingPhotos.map((photo, i) => (
        <FloatingAvatar key={i} photo={photo} inView={inView} />
      ))}

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-28 pb-20 w-full">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: `${TEAL}10`, border: `1px solid ${TEAL}20` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: TEAL }} />
            <span className="text-spaced !text-[10px]">A I &nbsp; I N F R A &nbsp; F O R &nbsp; H E A L T H C A R E</span>
          </motion.div>

          <motion.h1 variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={1}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-6" style={{ color: NAVY }}>
            <span style={{ color: TEAL }}>CareContext</span>
            <br />
            Patient-Owned AI Infrastructure for India
          </motion.h1>

          <motion.p variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={2}
            className="text-base sm:text-lg leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: MID }}>
            Patient owned medical records with AI-powered instant clinical context for doctors, enabling secure, efficient hospital workflows built on ABDM.
          </motion.p>

          <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={3}
            className="flex flex-wrap justify-center gap-4 mb-12">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button size="lg" className="btn-teal px-8 shadow-teal" onClick={(e) => scrollTo(e, "#demo")}>
                Book a Demo
                <motion.span className="inline-block ml-1" animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>→</motion.span>
              </Button>
            </motion.div>
            <Button variant="outline" size="lg" className="rounded-lg px-8 font-semibold hover:bg-[rgba(0,168,179,0.05)] transition-all"
              style={{ borderRadius: '8px', borderColor: TEAL, color: TEAL }} onClick={(e) => scrollTo(e, "#challenge")}>
              Learn More
            </Button>
          </motion.div>

          {/* Trust badges */}
          <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} custom={4}
            className="flex flex-wrap justify-center gap-6">
            {["85 Cr+ ABHA IDs", "5-sec Timelines", "DPDP Compliant"].map((t) => (
              <motion.div key={t} whileHover={{ y: -2, scale: 1.02 }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border shadow-sm"
                style={{ borderColor: `${TEAL}15` }}>
                <div className="w-2 h-2 rounded-full" style={{ background: '#28A745' }} />
                <span className="text-xs font-medium" style={{ color: MID }}>{t}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
